import React, { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { DataTable } from '../../ui/DataTable/DataTable';
import { SortableTh } from '../../ui/DataTable/SortableTh';
import type { CatalogEntry } from '../../../entities/productAnalogs/model/types';
import { AnalogsGroup } from './AnalogsGroup';
import { ProductRow } from './ProductRow';
import styles from './ProductAnalogsTable.module.scss';

interface ProductAnalogsTableProps {
    entries: CatalogEntry[];
    selectedId: string | null;
    expanded: Set<string>;
    footer?: ReactNode;
    empty?: ReactNode;
    isLoading?: boolean;
    loading?: boolean;
    loadingText?: string;
    scrollKey?: string | number;
    onSelect: (entry: CatalogEntry) => void;
    onToggleExpand: (id: string) => void;
}

export function ProductAnalogsTable({
    entries,
    selectedId,
    expanded,
    footer,
    empty,
    isLoading,
    loading,
    loadingText,
    scrollKey,
    onSelect,
    onToggleExpand,
}: ProductAnalogsTableProps) {
    const headRef = useRef<HTMLTableRowElement>(null);
    const [columnWidths, setColumnWidths] = useState<number[]>([]);

    useLayoutEffect(() => {
        const row = headRef.current;
        if (!row) return undefined;

        const measure = () => {
            const widths = Array.from(row.children, (cell) => cell.getBoundingClientRect().width);
            setColumnWidths((prev) =>
                prev.length === widths.length && prev.every((w, i) => Math.abs(w - widths[i]) < 0.5) ? prev : widths,
            );
        };

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(row);

        return () => observer.disconnect();
    }, []);

    return (
        <DataTable
            static
            footer={footer}
            empty={empty}
            isLoading={isLoading}
            loading={loading}
            loadingText={loadingText}
            scrollKey={scrollKey}
            className={styles.table}
            colgroup={
                <colgroup>
                    <col className={styles.colId} />
                    <col className={styles.colExpand} />
                    <col className={styles.colProduct} />
                    <col className={styles.colLinks} />
                    <col className={styles.colKind} />
                    <col />
                    <col />
                    <col />
                </colgroup>
            }
        >
            <thead>
                <tr ref={headRef}>
                    <th>ID</th>
                    <th aria-label="Раскрыть" />
                    <th>Товар</th>
                    <th>Связи</th>
                    <th>Тип</th>
                    <th>Проект</th>
                    <th>Дата добавления</th>
                    <th>Инициатор</th>
                </tr>
            </thead>

            <tbody>
                {entries.map((entry) => {
                    const isOpen = expanded.has(entry.id) && entry.role === 'main' && entry.analogsCount > 0;

                    return (
                        <React.Fragment key={entry.id}>
                            <ProductRow
                                entry={entry}
                                selected={selectedId === entry.id}
                                expanded={isOpen}
                                onSelect={onSelect}
                                onToggleExpand={onToggleExpand}
                            />

                            {isOpen && (
                                <AnalogsGroup
                                    entryId={entry.id}
                                    count={entry.analogsCount}
                                    selectedId={selectedId}
                                    columnWidths={columnWidths.slice(2)}
                                    onSelect={onSelect}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </DataTable >
    );
}